import { JobFrequencyEnum } from "./CreateJobAndSchedule.model";

/**
 * The Frequency dropdown's options, which are NOT the stored frequencies.
 *
 * Bi-weekly scheduling has always been expressible - "Recur every 2 week(s)" is
 * exactly it - but nobody found it, so the dropdown offers it as a preset over
 * the pair of fields that already exist. There is no BIWEEKLY on
 * `JobFrequencyEnum` and nothing new is persisted: bi-weekly IS `WEEKLY` with
 * `recurEvery: 2`.
 *
 * Because of that the dropdown's value is DERIVED from the two form fields on
 * every render and never stored. A job that loads with an interval of 3 shows
 * "Weekly" and keeps its 3; typing 3 into the interval spinner flips the label
 * back from "Bi-weekly" to "Weekly" on its own. There is no second source of
 * truth to reconcile, which is why the interval spinner stays visible and
 * editable - it is what makes every interval other than 1 and 2 reachable.
 */
export enum FrequencyPreset {
  WEEKLY = "WEEKLY",
  BIWEEKLY = "BIWEEKLY",
  MONTHLY = "MONTHLY",
  YEARLY = "YEARLY",
}

/** Bi-weekly sits between weekly and monthly, where someone scanning looks. */
export const frequencyPresetOptions = [
  { label: "Weekly", value: FrequencyPreset.WEEKLY },
  { label: "Bi-weekly", value: FrequencyPreset.BIWEEKLY },
  { label: "Monthly", value: FrequencyPreset.MONTHLY },
  { label: "Yearly", value: FrequencyPreset.YEARLY },
];

/**
 * Which option the dropdown shows for the frequency and interval now in the
 * form. Only a weekly job can read as bi-weekly, and only at an interval of
 * exactly 2 - every other weekly interval, including a cleared one, is
 * "Weekly".
 *
 * A form that has no frequency yet gets `null`, which leaves the dropdown
 * showing nothing: ContractForm registers the field with no default value at
 * all, and deriving "Weekly" there would show a choice the user had not made
 * and the form would not submit.
 *
 * `recurEvery` is numbered on every path that fills it, but it arrives from a
 * stored draft and from the API, so it is coerced rather than compared with
 * `===` against a possible "2".
 */
export const toFrequencyPreset = (
  frequency: JobFrequencyEnum | null | undefined,
  recurEvery: number | null | undefined
): FrequencyPreset | null => {
  if (frequency === JobFrequencyEnum.MONTHLY) {
    return FrequencyPreset.MONTHLY;
  }

  if (frequency === JobFrequencyEnum.YEARLY) {
    return FrequencyPreset.YEARLY;
  }

  if (frequency !== JobFrequencyEnum.WEEKLY) {
    return null;
  }

  return Number(recurEvery) === 2 ? FrequencyPreset.BIWEEKLY : FrequencyPreset.WEEKLY;
};

/**
 * What picking an option must write into the form.
 *
 * The two weekly presets own the interval - that is the whole point of them -
 * so picking "Weekly" writes 1 and "Bi-weekly" writes 2. Monthly and Yearly
 * carry no interval of their own and leave `recurEvery` exactly as it was,
 * which is what switching frequencies has always done. Picking Bi-weekly and
 * then Monthly therefore gives every 2 months; the spinner says so on screen.
 */
export const fromFrequencyPreset = (
  preset: FrequencyPreset,
  recurEvery: number | null | undefined
): { frequency: JobFrequencyEnum; recurEvery: number | null | undefined } => {
  switch (preset) {
    case FrequencyPreset.WEEKLY:
      return { frequency: JobFrequencyEnum.WEEKLY, recurEvery: 1 };
    case FrequencyPreset.BIWEEKLY:
      return { frequency: JobFrequencyEnum.WEEKLY, recurEvery: 2 };
    case FrequencyPreset.MONTHLY:
      return { frequency: JobFrequencyEnum.MONTHLY, recurEvery };
    case FrequencyPreset.YEARLY:
      return { frequency: JobFrequencyEnum.YEARLY, recurEvery };
  }
};
